/**
 * Funnel Outcomes Component
 *
 * This component manages the outcomes section of a funnel, allowing users to define
 * what happens when users complete the funnel with different answers.
 *
 * Features:
 * - Add, edit, and remove funnel outcomes
 * - Configure outcome properties (name, headline, text)
 * - Set outcome logic (direct or conditional)
 * - Integration with question-answer logic
 * - Confirmation dialogs for destructive actions
 *
 * Outcomes are displayed as an accordion, with each outcome expandable to show
 * its configuration options.
 *
 * @module components/funnel-form/funnel-outcomes/outcomes
 */

import {
  Row,
  Col,
  Card,
  Button,
  Accordion,
  InputGroup,
  useAccordionButton
} from "react-bootstrap";
import styled from "styled-components";
import {
  faTrashCan,
  faChevronUp,
  faChevronDown
} from "@fortawesome/free-solid-svg-icons";
import React, { memo, useRef, useState } from "react";
import CardHeader from "react-bootstrap/esm/CardHeader";
import AccordionItem from "react-bootstrap/esm/AccordionItem";
import AccordionBody from "react-bootstrap/esm/AccordionBody";
import { useFieldArray, useFormContext } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Legend,
  FieldSet,
  FixedWidthDiv,
  BootstrapCard,
  BorderlessInput
} from "../../common-styling";
import { useDialog } from "../../../hooks/useDialog";
import TextArea from "../../reusable-form-fields/text-area";
import { showSuccess } from "../../../services/toast-service";
import TextField from "../../reusable-form-fields/text-field";
import RadioInput from "../../reusable-form-fields/input-radio";
import { useTranslation } from "react-i18next";

/**
 * Styled accordion item container without borders or background
 */
const AccordionItemContainer = styled(AccordionItem)`
  border: none;
  background-color: transparent;
`;

/**
 * Styled card header with consistent styling
 */
const Header = styled(CardHeader)`
  background-color: #fff;
  border-radius: 0 !important;
`;

/**
 * Styled div that acts as the accordion toggle with proper cursor
 */
const AccordionToggleHandler = styled.div`
  cursor: pointer;
`;

/**
 * Styled icon with context-aware colors
 * Changes color based on icon type (delete vs. toggle)
 */
const Icon = styled(FontAwesomeIcon)`
  color: ${({ $icon_name }) =>
    $icon_name === "delete" ? "var(--danger-color)" : "var(--secondary-text)"};
  cursor: pointer;
`;

/**
 * Styled label for input groups with light background
 */
const GroupLabel = styled.div`
  background: #f5f8f9;
`;

/**
 * Styled accordion body with light background
 */
const OutcomeBody = styled(AccordionBody)`
  background: var(--body-light-bg);
`;

/**
 * Funnel outcomes component for managing outcome configurations
 *
 * @returns {JSX.Element} Funnel outcomes section
 */
const FunnelOutcomes = () => {
  const { t } = useTranslation();

  // Form context for accessing form methods and values
  const { getValues, control, register, setValue } = useFormContext();

  // Dialog context for confirmation dialogs
  const { showConfirmationDialogBox } = useDialog();

  // Field array for managing dynamic outcomes list
  const { fields, append, remove } = useFieldArray({
    control,
    name: "outcomes" // unique name for your Field Array
  });

  // Reference to store outcome being deleted for confirmation dialog
  const outcomeRef = useRef({});

  // State to track which accordion item is currently expanded
  const [activeKey, setActiveKey] = useState(null);
  /**
   * Show confirmation dialog when deleting an outcome that is in use
   */
  const toggleDialog = () =>
    showConfirmationDialogBox({
      title: t("common.confirmDeletion"),
      dialogType: "danger",
      dialogMessage: renderDialogMessage(),
      responseAction: dialogResponseAction
    });

  /**
   * Update answer actions that reference the deleted outcome
   * This ensures that no answers still reference a deleted outcome
   */
  const updateAnswerAction = () => {
    getValues("funnelQA").forEach((question, questionIdx) => {
      question.answers.forEach((answer, ansIdx) => {
        // If answer references the outcome being deleted, clear the reference
        if (answer.answerAction === outcomeRef.current.id) {
          setValue(
            `funnelQA[${questionIdx}].answers[${ansIdx}].answerAction`,
            ""
          );
        }
      });
    });
  };

  /**
   * Handle response from the confirmation dialog
   *
   * @param {boolean} action - Whether the user confirmed the action
   */
  const dialogResponseAction = (action) => {
    if (action) {
      // Remove the outcome
      remove(outcomeRef.current.index);
      // Update any answers that referenced this outcome
      updateAnswerAction();
      // Show success message
      showSuccess(`Outcome is deleted.`);
    }
  };

  /**
   * Render the confirmation dialog message
   *
   * @returns {JSX.Element} Dialog message content
   */
  const renderDialogMessage = () => (
    <>
      <div
        dangerouslySetInnerHTML={{
          __html: t("modalContent.deleteOutcome")
        }}
      ></div>
    </>
  );
  /**
   * Custom accordion toggle component that manages accordion state
   *
   * @param {Object} props - Component props
   * @param {string|number} props.eventKey - Accordion item key
   * @param {React.ReactNode} props.content - Content to display in the toggle
   * @returns {JSX.Element} Accordion toggle component
   */
  const ToggleAccordion = ({ eventKey, content }) => {
    // Create decorated click handler using the Accordion API
    const decoratedOnClick = useAccordionButton(eventKey, () => {
      // Toggle accordion state
      if (eventKey === activeKey) {
        setActiveKey(-1); // Close if already open
      } else {
        setActiveKey(eventKey); // Open if closed
      }
    });

    return (
      <AccordionToggleHandler onClick={decoratedOnClick}>
        {content}
      </AccordionToggleHandler>
    );
  };

  /**
   * Add a new outcome to the funnel
   * Generates a random ID and appends with default values
   */
  const addOutcome = () => {
    append({
      id: Math.random().toString(16).slice(2), // Generate random ID
      outcomeLogic: "directly", // Default to direct logic
      outcomeName: "",
      outcomeHeadline: "",
      outcomeText: ""
    });
  };

  /**
   * Delete an outcome from the funnel
   * If the outcome is referenced by any answers, shows a confirmation dialog
   *
   * @param {number} index - Index of the outcome to delete
   */
  const deleteOutcome = (index) => {
    // Get the outcome ID
    const id = getValues("outcomes")[index].id;
    // Store the outcome reference for the confirmation dialog
    outcomeRef.current = { index, id };

    // Check if the outcome is used in any answers
    let shouldDelete = true;
    getValues("funnelQA").forEach((qa) => {
      const found = qa.answers.find((answer) => answer.answerAction === id);
      if (found) {
        shouldDelete = false;
      }
    });

    // Delete directly if not used, otherwise show confirmation
    if (shouldDelete) {
      remove(index);
    } else {
      toggleDialog();
    }
  };

  return (
    <div className="py-3 px-0">
      <FieldSet>
        <Legend>
          <h6 className="mb-0 fw-bold d-flex align-center">Outcomes</h6>
        </Legend>
        <div className="d-flex justify-content-end">
          <Button variant="primary" onClick={addOutcome}>
            {t("addNewOutcome")}
          </Button>
        </div>
        <Accordion flush>
          {fields.map((outcome, index) => (
            <AccordionItemContainer
              key={outcome.id}
              eventKey={index}
              className="p-0 m-0"
            >
              <BootstrapCard className="mt-3" $showShadow={true}>
                <Header className="p-0">
                  <div className="d-flex align-items-center">
                    <FixedWidthDiv className="text-center" $width="40">
                      <ToggleAccordion
                        eventKey={index}
                        content={
                          activeKey === index ? (
                            <Icon icon={faChevronUp} />
                          ) : (
                            <Icon icon={faChevronDown} />
                          )
                        }
                      />
                    </FixedWidthDiv>
                    <div className="flex-fill p-0 border-start align-self-stretch">
                      <InputGroup className="">
                        <GroupLabel className="d-flex align-items-center text-secondary px-2 fw-bold">
                          Outcome {t("common.name")}:
                        </GroupLabel>
                        <BorderlessInput
                          type="text"
                          className="border-start border-end"
                          placeholder={`${t("common.enter")} Outcome-Name`}
                          name={`outcomes.${index}.outcomeName`}
                          {...register(`outcomes.${index}.outcomeName`)}
                        />
                      </InputGroup>
                    </div>
                    <FixedWidthDiv className="text-center" $width="40">
                      <Icon
                        onClick={() => deleteOutcome(index)}
                        icon={faTrashCan}
                        $icon_name="delete"
                      />
                    </FixedWidthDiv>
                  </div>
                </Header>
                <OutcomeBody>
                  <Card.Body className="px-0">
                    <Row>
                      <Col xs={12} md={6}>
                        <RadioInput
                          isInline={false}
                          label={`Outcome-${t("common.logic")}:`}
                          firstOptLabel={t("outcome.jumpDirect")}
                          secondOptLabel={t("outcome.jumpEnd")}
                          thirdOptLabel={t("outcome.restart")}
                          firstValue="directly"
                          firstValueId={`directly-${index}`}
                          secondValue="end"
                          secondValueId={`end-${index}`}
                          thirdValue="restart"
                          thirdValueId={`restart-${index}`}
                          name={`outcomes[${index}].outcomeLogic`}
                          register={register}
                        />
                      </Col>
                      <Col xs={12} md={6}>
                        <TextField
                          type="text"
                          placeholder={`Outcome-${t("common.headline")}`}
                          label={`Outcome-${t("common.headline")}:`}
                          name={`outcomes[${index}].outcomeHeadline`}
                          register={register}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={12} md={6}>
                        <TextArea
                          name={`outcomes[${index}].outcomeText`}
                          label="Outcome Text:"
                          register={register}
                          noSpacing
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </OutcomeBody>
              </BootstrapCard>
            </AccordionItemContainer>
          ))}
        </Accordion>
      </FieldSet>
    </div>
  );
};
export default memo(FunnelOutcomes);
